// Single choke point for all analytics events.
// Nothing else in the app may touch window.dataLayer or window.gtag directly.

// --- Event taxonomy (29 names) ------------------------------------------------
export type AnalyticsEvent =
  | 'section_view'
  | 'section_engaged'
  | 'scroll_depth'
  | 'page_exit'
  | 'nav_click'
  | 'hero_cta_click'
  | 'carousel_interact'
  | 'room_select'
  | 'builders_cta_click'
  | 'assistant_open'
  | 'assistant_prompt_click'
  | 'assistant_message_sent'
  | 'assistant_response'
  | 'assistant_handoff'
  | 'assistant_close'
  | 'assistant_lead_captured'
  | 'form_start'
  | 'form_field_complete'
  | 'form_field_error'
  | 'consent_toggle'
  | 'form_abandon'
  | 'form_error'
  | 'generate_lead'
  | 'callback_request'
  | 'phone_click'
  | 'email_click'
  | 'consent_updated'
  | 'error_boundary_shown'
  | 'web_vitals'
  | 'estimator_open'
  | 'estimator_step_complete'
  | 'estimator_item_toggle'
  | 'estimator_estimate_shown'
  | 'estimator_abandon'
  | 'estimator_lead_captured'
  | 'guide_view'
  | 'contact_form_submit';

// --- Shared parameter vocabulary (21 names) ------------------------------------
export type AnalyticsParamKey =
  | 'section_name'
  | 'element_label'
  | 'element_location'
  | 'interaction_method'
  | 'sequence_index'
  | 'duration_ms'
  | 'scroll_percent'
  | 'funnel_step'
  | 'field_name'
  | 'error_type'
  | 'assistant_trigger'
  | 'message_index'
  | 'latency_ms'
  | 'room_label'
  | 'slide_index'
  | 'lead_source'
  | 'reference_id'
  | 'is_error'
  | 'fields_completed'
  | 'requirement_length'
  | 'consent_analytics'
  | 'guide_slug'
  | 'guide_title'
  | 'form_location';

export type AnalyticsParams = Partial<Record<AnalyticsParamKey, string | number | boolean>>;

// --- PII allowlist: only these 21 param keys may leave the app ---------------
const ALLOWED_PARAM_KEYS: readonly string[] = [
  'section_name',
  'element_label',
  'element_location',
  'interaction_method',
  'sequence_index',
  'duration_ms',
  'scroll_percent',
  'funnel_step',
  'field_name',
  'error_type',
  'assistant_trigger',
  'message_index',
  'latency_ms',
  'room_label',
  'slide_index',
  'lead_source',
  'reference_id',
  'is_error',
  'fields_completed',
  'requirement_length',
  'consent_analytics',
  'guide_slug',
  'guide_title',
  'form_location',
];

// Keys that are NEVER allowed (PII) — if passed, strip silently.
const PII_KEYS: readonly string[] = ['name', 'phone', 'email'];

// --- Consent state -------------------------------------------------------------
let _consentGranted = false;
let _consentCallbacks: Array<(granted: boolean) => void> = [];

interface WindowWithDataLayer extends Window {
  dataLayer: unknown[];
  // Defined by the Consent Mode v2 bootstrap in index.html.
  gtag?: (...args: unknown[]) => void;
}

export function updateConsent(granted: boolean) {
  _consentGranted = granted;

  if (typeof window !== 'undefined') {
    const w = window as unknown as WindowWithDataLayer;

    // The actual Consent Mode v2 signal. Without this, Google tags ignore the
    // banner entirely and set cookies regardless of what the visitor chose.
    if (typeof w.gtag === 'function') {
      const state = granted ? 'granted' : 'denied';
      w.gtag('consent', 'update', {
        ad_storage: state,
        ad_user_data: state,
        ad_personalization: state,
        analytics_storage: state,
      });
    }

    // Also emit a plain dataLayer event so GTM can trigger on consent changes.
    if (w.dataLayer) {
      w.dataLayer.push({
        event: 'consent_update',
        consent_analytics: granted ? 'granted' : 'denied',
      });
    }
  }

  _consentCallbacks.forEach((cb) => cb(granted));
}

export function isConsentGranted(): boolean {
  return _consentGranted;
}

export function onConsentChange(cb: (granted: boolean) => void) {
  _consentCallbacks.push(cb);
  return () => {
    _consentCallbacks = _consentCallbacks.filter((f) => f !== cb);
  };
}

// --- Track function (single choke point) ---------------------------------------
export function track(event: AnalyticsEvent, params?: AnalyticsParams): void {
  if (typeof window === 'undefined') return;

  // Always push the event object to dataLayer so GTM can inspect it.
  // If consent is denied, GTM's Consent Mode still sends cookieless pings.
  const dl = (window as unknown as WindowWithDataLayer).dataLayer;
  if (!dl) return;

  const clean: Record<string, string | number | boolean> = {};
  if (params) {
    for (const key of Object.keys(params)) {
      // Silently drop PII keys
      if (PII_KEYS.includes(key)) continue;
      // Only allow the shared vocabulary keys
      if (ALLOWED_PARAM_KEYS.includes(key)) {
        const val = params[key as AnalyticsParamKey];
        if (val !== undefined && val !== null && val !== '') {
          clean[key] = val;
        }
      }
    }
  }

  dl.push({
    event,
    ...clean,
  });
}

// --- Bootstrap dataLayer -------------------------------------------------------
export function bootstrapDataLayer(): void {
  if (typeof window === 'undefined') return;
  const w = window as unknown as WindowWithDataLayer;
  w.dataLayer = w.dataLayer || [];
  // NOTE: the real Consent Mode v2 defaults are set in index.html, before the
  // GTM container loads. This event is only a convenience trigger for GTM.
  w.dataLayer.push({
    event: 'consent_default',
    consent_analytics: 'denied',
  });
}
